import {
  CollectionReference,
  type DocumentData,
  type DocumentReference,
  serverTimestamp,
} from 'firebase/firestore'
import {
  collection,
  doc,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import {
  convertDocumentSnapshotToModel,
  modelItemStream,
  modelListStream,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SlideRubric } from '../../models/SlideRubric'
import type { FirestoreSlideRubric } from './schema'
import { schema, type writeSchema } from './schema'
import { type z } from 'zod'
import {
  addDocWithError,
  deleteDocWithError,
  getDocsWithError,
  getDocWithError,
  updateDocWithError,
} from '../../firestore-mobx/fetch'
import { type RubricType } from '../PromptRubricType'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSlideRubric> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

const getColRef = (
  firestore: Firestore,
  { slideDeckId }: { slideDeckId: string }
): CollectionReference<FirestoreSlideRubric, DocumentData> => {
  return collection(
    firestore,
    'slide_deck',
    slideDeckId,
    'rubric'
  ).withConverter(converter)
}

const getDocRef = (
  firestore: Firestore,
  { slideDeckId, slideRubricId }: { slideDeckId: string; slideRubricId: string }
): DocumentReference<FirestoreSlideRubric, DocumentData> => {
  return doc(getColRef(firestore, { slideDeckId: slideDeckId }), slideRubricId)
}

export const getSlideRubrics = (
  repository: FirebaseRepository,
  params: { slideDeckId: string }
) => {
  const ref = getColRef(repository.firestore, params)

  return modelListStream(repository, ref, SlideRubric)
}

export const fetchSlideRubrics = async (
  repository: FirebaseRepository,
  params: { slideDeckId: string }
) => {
  const docRef = getColRef(repository.firestore, params)
  const docs = await getDocsWithError(docRef, 'FetchSlideRubricsError')
  return docs.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, SlideRubric)
  )
}

export const getSlideDeckRubric = (
  repository: FirebaseRepository,
  params: { slideDeckId: string; slideRubricId: string }
) => {
  const ref = getDocRef(repository.firestore, params)

  return modelItemStream(repository, ref, SlideRubric)
}

export const deleteSlideRubric = async (
  repository: FirebaseRepository,
  { slideRubricId, slideDeckId }: { slideRubricId: string; slideDeckId: string }
) => {
  await deleteDocWithError(
    getDocRef(repository.firestore, { slideRubricId, slideDeckId }),
    'DeleteSlideRubricError'
  )
}

/**
 * prompt/rubric_prompt/type/{rubricType}/prompt_version/{promptVersionId}/test/{testId}/result/{resultId}/test_rubric
 *
 * returns null if no fake rubric found
 */
export const fetchRubricPromptTestRubric = async (
  repository: FirebaseRepository,
  {
    rubricType,
    promptVersionId,
    testId,
  }: {
    rubricType: RubricType
    promptVersionId: string
    testId: string
  }
) => {
  // there should be one result under each test
  const resultsRef = collection(
    repository.firestore,
    'prompt',
    'rubric_prompt',
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test',
    testId,
    'result'
  )

  const resultsSnap = await getDocsWithError(
    resultsRef,
    'FetchRubricPromptTestRubricResultError'
  )

  if (resultsSnap.empty)
    throw new Error(
      `No results found for rubric type ${rubricType}, version ${promptVersionId}, test ${testId}`
    )

  if (resultsSnap.size > 1)
    throw new Error(
      `Multiple results found for rubric type ${rubricType}, version ${promptVersionId}, test ${testId}`
    )

  const resultDoc = resultsSnap.docs[0]
  const resultId = resultDoc.id

  const ref = doc(
    repository.firestore,
    'prompt',
    'rubric_prompt',
    'type',
    rubricType.toString(),
    'prompt_version',
    promptVersionId,
    'test',
    testId,
    'result',
    resultId,
    'test_rubric',
    'fake-rubric'
  ).withConverter(converter)

  const snapshot = await getDocWithError(
    ref,
    'FetchRubricPromptTestRubricError'
  )

  if (!snapshot.exists()) return null

  return convertDocumentSnapshotToModel(repository, snapshot, SlideRubric)
}

export const saveSlideRubric = async (
  repository: FirebaseRepository,
  {
    slideDeckId,
    rubricId,
    rubricFields,
  }: {
    rubricFields: Omit<z.infer<typeof writeSchema>, 'updatedAt'>
    slideDeckId: string
    rubricId?: string
  }
) => {
  const dataToWrite = {
    ...rubricFields,
    updatedAt: serverTimestamp(),
  }

  const ref = rubricId
    ? getDocRef(repository.firestore, { slideDeckId, slideRubricId: rubricId })
    : getColRef(repository.firestore, { slideDeckId })

  if (ref instanceof CollectionReference) {
    return await addDocWithError(ref, dataToWrite, 'CreateSlideRubricError')
  }

  return await updateDocWithError(ref, dataToWrite, 'UpdateSlideRubricError')
}

export const assignSlideRubricToSlide = async (
  repository: FirebaseRepository,
  {
    startSlideId,
    slideId,
    slideDeckId,
    rubricId,
  }: {
    startSlideId?: string
    slideId?: string
    slideDeckId: string
    rubricId: string
  }
) => {
  const ref = getDocRef(repository.firestore, {
    slideDeckId,
    slideRubricId: rubricId,
  })

  await updateDocWithError(
    ref,
    {
      startSlideId,
      slideId,
      updatedAt: serverTimestamp(),
    },
    'AssignSlideRubricToSlideError'
  )
}
